import { useMemo } from "react";

/**
 * Simple 3-dropdown date picker (día / mes / año) — más fácil que el nativo <input type="date">
 * en dispositivos móviles.
 *
 * Props:
 *  - value:    string "YYYY-MM-DD" (ISO) o ""
 *  - onChange: (isoString) => void  — llamada cuando los 3 están seleccionados
 *  - minYear:  default 1920
 *  - maxYear:  default current year
 *  - testId:   prefix para data-testid
 */
const MESES = [
  { v: "01", l: "Enero" }, { v: "02", l: "Febrero" }, { v: "03", l: "Marzo" },
  { v: "04", l: "Abril" }, { v: "05", l: "Mayo" }, { v: "06", l: "Junio" },
  { v: "07", l: "Julio" }, { v: "08", l: "Agosto" }, { v: "09", l: "Septiembre" },
  { v: "10", l: "Octubre" }, { v: "11", l: "Noviembre" }, { v: "12", l: "Diciembre" },
];

export default function DateDropdown({ value = "", onChange, minYear = 1920, maxYear, testId = "date" }) {
  const currentYear = new Date().getFullYear();
  const maxY = maxYear ?? currentYear;

  const [y, m, d] = (value || "").split("-");

  const years = useMemo(() => {
    const arr = [];
    for (let yy = maxY; yy >= minYear; yy--) arr.push(String(yy));
    return arr;
  }, [maxY, minYear]);

  const daysInMonth = useMemo(() => {
    if (!y || !m) return 31;
    return new Date(Number(y), Number(m), 0).getDate();
  }, [y, m]);

  const update = (part, val) => {
    const nextY = part === "y" ? val : y || "";
    const nextM = part === "m" ? val : m || "";
    let nextD = part === "d" ? val : d || "";
    // Guard: if the day is greater than the new month's max, reset it
    if (nextY && nextM) {
      const max = new Date(Number(nextY), Number(nextM), 0).getDate();
      if (Number(nextD) > max) nextD = "";
    }
    if (nextY && nextM && nextD) {
      onChange(`${nextY}-${nextM}-${nextD.padStart(2, "0")}`);
    } else {
      onChange("");
    }
  };

  const selectStyle = { padding: "10px 8px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, background: "white", fontFamily: "inherit" };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr", gap: 8 }}>
      <select
        value={d || ""} onChange={(e) => update("d", e.target.value)}
        style={selectStyle} data-testid={`${testId}-dia`}
      >
        <option value="">Día</option>
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((n) => (
          <option key={n} value={String(n).padStart(2, "0")}>{n}</option>
        ))}
      </select>
      <select
        value={m || ""} onChange={(e) => update("m", e.target.value)}
        style={selectStyle} data-testid={`${testId}-mes`}
      >
        <option value="">Mes</option>
        {MESES.map((mm) => (
          <option key={mm.v} value={mm.v}>{mm.l}</option>
        ))}
      </select>
      <select
        value={y || ""} onChange={(e) => update("y", e.target.value)}
        style={selectStyle} data-testid={`${testId}-anio`}
      >
        <option value="">Año</option>
        {years.map((yy) => (
          <option key={yy} value={yy}>{yy}</option>
        ))}
      </select>
    </div>
  );
}
